
import axios from 'axios';
import { apiConfig } from '@/lib/env';

// Use centralized API configuration - no more direct env access
export { apiConfig };

// Backend requires a Bearer JWT on every lead/pipeline/scheduler route.
// Only for our own API, so the JWT never leaks to third-party URLs.
export const withAuthToken = (config: any) => {
  const token = localStorage.getItem('token');
  const url = `${config.baseURL || ''}${config.url || ''}`;
  const ownApi = !/^https?:\/\//.test(url) || url.startsWith(new URL(apiConfig.baseUrl, window.location.origin).origin);
  if (token && ownApi && !config.headers?.Authorization) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
};

const createApiInstance = () => {
  // Validate URL before creating instance - using centralized config
  if (apiConfig.baseUrl.includes('ai.lnk2.im') || apiConfig.baseUrl.includes('9001')) {
    console.error('❌ CRITICAL: Attempting to create axios instance with old URL!', apiConfig.baseUrl);
    throw new Error(`Invalid API baseURL: ${apiConfig.baseUrl}`);
  }

  const instance = axios.create({
    // Use the centralized API configuration
    baseURL: apiConfig.baseUrl,
    headers: {
      'Content-Type': 'application/json',
    },
    timeout: 10000,
  });

  // Add request interceptor for logging and validation
  instance.interceptors.request.use(
    (config) => {
      const fullUrl = `${config.baseURL}${config.url}`;
      
      // Block requests to old API
      if (fullUrl.includes('ai.lnk2.im') || fullUrl.includes('9001')) {
        console.error('❌ BLOCKED: Axios request to old API URL!', fullUrl);
        throw new Error(`Blocked request to old API URL: ${fullUrl}`);
      }
      
      console.log(`📡 Axios Request: ${config.method?.toUpperCase()} ${fullUrl}`);
      return withAuthToken(config);
    },
    (error) => {
      console.error('❌ Axios Request Error:', error);
      return Promise.reject(error);
    }
  );

  // Add response interceptor for logging
  instance.interceptors.response.use(
    (response) => {
      const fullUrl = `${response.config.baseURL}${response.config.url}`;
      console.log(`✅ Axios Response from ${fullUrl}:`, response.status);
      return response;
    },
    (error) => {
      if (error.response) {
        const fullUrl = `${error.config?.baseURL}${error.config?.url}`;
        console.error(`❌ Axios Response Error: ${error.response.status} ${error.response.statusText} for ${fullUrl}`);
      } else {
        console.error('❌ Axios Network Error:', error.message);
      }
      return Promise.reject(error);
    }
  );

  return instance;
};

// Export centralized API URL for non-axios direct requests
export const API_URL = apiConfig.baseUrl;

export default createApiInstance;
